import { ColumnFilter, ColumnSort } from '@tanstack/react-table';
import axiosInstance from 'config/axios';
import { User } from 'interfaces/user';
import { PaginationResponse } from 'interfaces/pagination-response';

/**
 * @description User response type
 * @type {UserResponse}
 */
export type UserResponse = PaginationResponse<User>;

/**
 * @description User service
 * @type {UserService}
 */
const UserService = {
  /**
   * @description Get the currently authenticated user's profile information
   *
   * @SADtrace SAD-003-2.1, SAD-003-2.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @returns {Promise<User>} Current user profile
   */
  getMe: () => {
    return axiosInstance.get('/v1/users/me');
  },

  /**
   * @description Update the currently authenticated user's profile information
   *
   * @SADtrace SAD-003-2.1, SAD-003-2.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Partial<User>} data - User data to update
   *
   * @returns {Promise<User>} Updated user profile
   */
  updateMe: (data: Partial<User>) => {
    return axiosInstance.patch<User>('/v1/users/me', data);
  },

  /**
   * @description Update the currently authenticated user's password
   *
   * @SADtrace SAD-003-2.1, SAD-003-2.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Object} data - Password update data containing old and new passwords
   *
   * @returns {Promise<User>} Updated user profile
   */
  updateMyPassword(data: {
    old_password: string | null;
    new_password: string | null;
  }) {
    return axiosInstance.patch<User>('/v1/users/me/password', data);
  },

  /**
   * @description Create a new user in the system (Admin only)
   *
   * @SADtrace SAD-003-2.1, SAD-003-2.3
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Partial<User>} data - User data for the new user
   *
   * @returns {Promise<User>} Created user
   */
  create: (data: Partial<User>) => {
    return axiosInstance.post<User>('/v1/users', data);
  },

  /**
   * @description Update an existing user in the system (Admin only)
   *
   * @SADtrace SAD-003-2.1, SAD-003-2.4
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - User id
   * @param {Partial<User>} data - User data to update
   *
   * @returns {Promise<User>} Updated user
   */
  update: (id: string | number, data: Partial<User>) => {
    return axiosInstance.patch<User>(`/v1/users/${id}`, data);
  },

  /**
   * @description Find a specific user by ID (Admin only)
   *
   * @SADtrace SAD-003-2.1, SAD-003-2.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - User id
   *
   * @returns {Promise<User>} User data
   */
  find: (id: string | number) => {
    return axiosInstance.get<User>(`/v1/users/${id}`);
  },

  /**
   * @description Delete a user from the system (Admin only)
   *
   * @SADtrace SAD-003-2.1, SAD-003-2.5
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string | number} id - User id
   *
   * @returns {Promise<User>} Deleted user data
   */
  delete: (id: string | number) => {
    return axiosInstance.delete<User>(`/v1/users/${id}`);
  },

  /**
   * @description Get paginated list of users with sorting and filtering (Admin only)
   *
   * @SADtrace SAD-003-2.1, SAD-003-2.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Object} queryParams - Query parameters including pagination, sorting, and filters
   *
   * @returns {Promise<UserResponse>} Paginated user response
   */
  get: (queryParams: {
    pageIndex: number;
    pageSize: number;
    sorting?: ColumnSort[];
    columnFilters: ColumnFilter[];
  }) => {
    return axiosInstance.get<UserResponse>('/v1/users', {
      params: queryParams,
    });
  },
};

export default UserService;
